import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { CurrencyInr, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

/* ============================================================================
   PatientInvoicesTab — HQ-wide read view of every provider→patient invoice.
   Lives inside /admin/finance. Lets ops/finance scan the clinic-side ledger
   without having to impersonate a doctor. Status filter + free-text search.
   ========================================================================== */

const STATUS_TONE = {
    draft:   { label: "Draft",   cls: "bg-slate-100 text-slate-700 border-slate-200" },
    sent:    { label: "Sent",    cls: "bg-amber-50 text-amber-800 border-amber-200" },
    partial: { label: "Partial", cls: "bg-amber-100 text-amber-900 border-amber-300" },
    paid:    { label: "Paid",    cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    void:    { label: "Void",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const STATUS_TABS = ["all", "sent", "partial", "paid", "void"];

const formatINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDT = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";


export function PatientInvoicesTab() {
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        // Load all invoices; filter status client-side so chip counts stay accurate
        api.get("/admin/finance/patient-invoices")
            .then((r) => setRows(r.data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, []);

    const statusCounts = useMemo(() => {
        const c = { all: rows.length, sent: 0, partial: 0, paid: 0, void: 0, draft: 0 };
        rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
        return c;
    }, [rows]);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (status !== "all" && r.status !== status) return false;
            if (!ql) return true;
            return (r.code || "").toLowerCase().includes(ql)
                || (r.patient_name || "").toLowerCase().includes(ql)
                || (r.doctor_name || "").toLowerCase().includes(ql)
                || (r.clinic_name || "").toLowerCase().includes(ql);
        });
    }, [rows, status, q]);

    return (
        <div className="space-y-4" data-testid="patient-invoices-tab">
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search invoice, patient, doctor, clinic…"
                searchTestid="patient-invoices-search"
                testid="patient-invoices-search-bar"
            />

            <FilterChipRow
                testid="patient-invoices-chip-row"
                chipTestidPrefix="patient-invoices-filter"
                value={status}
                onChange={setStatus}
                chips={[
                    { id: "all",     label: "All",     count: statusCounts.all,     tone: "primary" },
                    { id: "sent",    label: "Sent",    count: statusCounts.sent,    tone: "amber" },
                    { id: "partial", label: "Partial", count: statusCounts.partial, tone: "amber" },
                    { id: "paid",    label: "Paid",    count: statusCounts.paid,    tone: "emerald" },
                    { id: "void",    label: "Void",    count: statusCounts.void,    tone: "rose" },
                ]}
            />

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-2">Invoice</div>
                    <div className="col-span-2">Patient</div>
                    <div className="col-span-3">Clinic / Doctor</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 text-right">Total / Balance</div>
                    <div className="col-span-1 text-right">Issued</div>
                </div>
                {loading ? (
                    <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground text-sm" data-testid="patient-invoices-empty">
                        No invoices match your filters.
                    </div>
                ) : (
                    filtered.map((inv) => {
                        const t = STATUS_TONE[inv.status] || STATUS_TONE.draft;
                        const balance = (inv.total || 0) - (inv.amount_paid || 0);
                        return (
                            <div
                                key={inv.id}
                                className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition"
                                data-testid={`patient-invoice-row-${inv.id}`}
                            >
                                <div className="col-span-2 font-mono text-xs font-semibold">{inv.code}</div>
                                <div className="col-span-2 min-w-0 text-sm truncate">{inv.patient_name || "—"}</div>
                                <div className="col-span-3 min-w-0">
                                    <Link to={`/admin/doctors/${inv.doctor_id}`} className="text-sm font-medium truncate block hover:text-primary" title={inv.clinic_name}>
                                        {inv.clinic_name || "—"}
                                    </Link>
                                    <div className="text-[11px] text-muted-foreground truncate">{inv.doctor_name || "—"}</div>
                                </div>
                                <div className="col-span-2">
                                    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${t.cls}`}>{t.label}</span>
                                </div>
                                <div className="col-span-2 text-right">
                                    <div className="font-bold tabular-nums">{formatINR(inv.total)}</div>
                                    {balance > 0 && <div className="text-[11px] text-rose-600 tabular-nums">{formatINR(balance)} due</div>}
                                </div>
                                <div className="col-span-1 text-right text-xs text-muted-foreground tabular-nums">{fmtDT(inv.issued_at || inv.created_at)}</div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
